import { supabase } from '../lib/supabase';
import { createFileUploadNotification, createStageCompletionNotification, createDeadlineWarningNotification } from './notificationService';


export const teamStageUploadsService = {
  // Get all project stages for dropdown
  async getProjectStages() {
    const { data, error } = await supabase?.from('project_stages')?.select('*')?.eq('is_active', true)?.order('display_order');
    
    if (error) throw error;
    
    // Convert to camelCase
    return data?.map(stage => ({
      id: stage?.id,
      name: stage?.name,
      description: stage?.description,
      displayOrder: stage?.display_order,
      isActive: stage?.is_active,
      createdAt: stage?.created_at
    })) || [];
  },

  // Get uploads for a specific team
  async getTeamUploads(teamId) {
    const { data, error } = await supabase?.from('team_stage_uploads')?.select(`
        *,
        project_stages(name, description),
        user_profiles!inner(full_name, email)
      `)?.eq('team_id', teamId)?.order('uploaded_at', { ascending: false });
    
    if (error) throw error;
    
    // Convert to camelCase and flatten
    return data?.map(upload => ({
      id: upload?.id,
      teamId: upload?.team_id,
      stageId: upload?.stage_id,
      stageName: upload?.project_stages?.name || 'General Upload',
      stageDescription: upload?.project_stages?.description,
      uploadedBy: upload?.uploaded_by,
      uploaderName: upload?.user_profiles?.full_name,
      uploaderEmail: upload?.user_profiles?.email,
      fileUrl: upload?.file_url,
      notes: upload?.notes,
      uploadedAt: upload?.uploaded_at
    })) || [];
  },

  // Get team-level uploads only (not stage-specific)
  async getTeamLevelUploads(teamId) {
    const { data, error } = await supabase?.from('team_stage_uploads')?.select(`
        *,
        user_profiles!inner(full_name, email, role)
      `)?.eq('team_id', teamId)?.is('stage_id', null)?.order('uploaded_at', { ascending: false });
    
    if (error) throw error;
    
    // Convert to camelCase and flatten
    return data?.map(upload => ({
      id: upload?.id,
      teamId: upload?.team_id,
      uploadedBy: upload?.uploaded_by,
      uploaderName: upload?.user_profiles?.full_name,
      uploaderEmail: upload?.user_profiles?.email,
      uploaderRole: upload?.user_profiles?.role,
      fileUrl: upload?.file_url,
      notes: upload?.notes,
      uploadedAt: upload?.uploaded_at
    })) || [];
  },

  // Check if current user can delete an upload
  async canDeleteUpload(uploadId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return false;

      // Get user profile to check role
      const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
      
      // PCF Admin can delete any upload
      if (profile?.role === 'pcf_admin') return true;

      // Get upload to check uploader
      const { data: upload } = await supabase?.from('team_stage_uploads')?.select('uploaded_by')?.eq('id', uploadId)?.single();
      
      // User can delete their own uploads
      return upload?.uploaded_by === user?.id;
    } catch (error) {
      console.error('Error checking delete permission:', error);
      return false;
    }
  },

  // Upload file and create database record
  async uploadStageFile(teamId, stageId, file, description) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate file path
      const timestamp = Date.now();
      const sanitizedFileName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${user?.id}/${timestamp}_${sanitizedFileName}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('team-stage-files')?.upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record with only existing columns
      const { data, error } = await supabase?.from('team_stage_uploads')?.insert({
          team_id: teamId,
          stage_id: stageId,
          uploaded_by: user?.id,
          file_url: uploadData?.path,
          notes: description || null
        })?.select(`
          *,
          project_stages!inner(name),
          user_profiles!inner(full_name)
        `)?.single();

      if (error) throw error;

      // Extract file name from path for display
      const displayFileName = file?.name || uploadData?.path?.split('/')?.pop() || 'Unknown file';

      // Convert to camelCase
      return {
        id: data?.id,
        teamId: data?.team_id,
        stageId: data?.stage_id,
        stageName: data?.project_stages?.name,
        uploadedBy: data?.uploaded_by,
        uploaderName: data?.user_profiles?.full_name,
        fileName: displayFileName,
        fileUrl: data?.file_url,
        notes: data?.notes,
        uploadedAt: data?.uploaded_at
      };
    } catch (error) {
      // Clean up uploaded file if database insert failed
      if (error?.message !== 'Not authenticated') {
        try {
          await supabase?.storage?.from('team-stage-files')?.remove([filePath]);
        } catch (cleanupError) {
          console.warn('Failed to clean up uploaded file:', cleanupError);
        }
      }
      throw error;
    }
  },

  // Get signed URL for file download
  async getFileUrl(filePath) {
    const { data, error } = await supabase?.storage?.from('team-stage-files')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw error;
    return data?.signedUrl;
  },

  // Delete file and database record
  async deleteUpload(uploadId, filePath) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Delete database record (this checks ownership via RLS)
    const { error: dbError } = await supabase?.from('team_stage_uploads')?.delete()?.eq('id', uploadId);

    if (dbError) throw dbError;

    // Delete file from storage
    const { error: storageError } = await supabase?.storage?.from('team-stage-files')?.remove([filePath]);

    if (storageError) {
      console.warn('Failed to delete file from storage:', storageError);
    }

    return true;
  },

  // Update upload description
  async updateDescription(uploadId, description) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('team_stage_uploads')?.update({ description: description || '' })?.eq('id', uploadId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      description: data?.description,
      updatedAt: data?.updated_at
    };
  }
};

/**
 * Upload a file for a team stage
 * @param {Object} uploadData - Upload information
 * @returns {Promise<Object>} Upload result
 */
export const uploadTeamStageFile = async (uploadData) => {
  try {
    const { teamId, stageName, fileName, fileUrl } = uploadData;

    // Insert upload record
    const { data, error } = await supabase
      ?.from('team_stage_uploads')
      ?.insert({
        team_id: teamId,
        stage_name: stageName,
        file_name: fileName,
        file_url: fileUrl
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Get team name for notification
    const { data: team } = await supabase
      ?.from('teams')
      ?.select('team_name')
      ?.eq('id', teamId)
      ?.single();

    // Send notification to admins
    await createFileUploadNotification(teamId, stageName, team?.team_name || 'Unknown Team');

    // Check if stage is complete
    const { count } = await supabase
      ?.from('team_stage_uploads')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('team_id', teamId)
      ?.eq('stage_name', stageName);

    // Assuming each stage requires specific number of uploads (e.g., 3)
    const requiredUploads = 3;
    if (count >= requiredUploads) {
      await createStageCompletionNotification(teamId, stageName, team?.team_name || 'Unknown Team');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error uploading team stage file:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Check for teams approaching deadlines without uploads
 * Should be called periodically (e.g., daily cron job)
 */
export const checkTeamDeadlines = async () => {
  try {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    tomorrow?.setHours(23, 59, 59, 999);

    // Get all teams with their stage deadlines
    const { data: teams, error } = await supabase
      ?.from('teams')
      ?.select('id, team_name, current_stage, stage_deadline');

    if (error) throw error;

    for (const team of teams || []) {
      if (!team?.stage_deadline) continue;

      const deadline = new Date(team?.stage_deadline);
      
      // Check if deadline is tomorrow
      if (deadline?.toDateString() === tomorrow?.toDateString()) {
        // Check if team has uploaded anything for current stage
        const { count } = await supabase
          ?.from('team_stage_uploads')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('team_id', team?.id)
          ?.eq('stage_name', team?.current_stage);

        if (count === 0) {
          await createDeadlineWarningNotification(
            team?.id,
            team?.current_stage,
            team?.team_name
          );
        }
      }
    }
  } catch (error) {
    console.error('Error checking team deadlines:', error);
  }
};

/**
 * NEW: Send notification for deliverable completion
 * @param {Object} notificationData - Notification details
 */
export const sendDeliverableCompletionNotification = async (notificationData) => {
  try {
    const { teamId, deliverableName, teamName } = notificationData;

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-file-upload-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          uploaderName: 'System',
          teamName: teamName,
          teamNumber: '',
          fileName: `Deliverable: ${deliverableName}`,
          timestamp: new Date()?.toISOString(),
          stage: 'Deliverable Completion',
          fileUrl: '',
          teamId: teamId,
          notificationType: 'deliverable_completion',
          message: `Team ${teamName} has completed the deliverable: ${deliverableName}`
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('Deliverable completion notification failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('Deliverable completion notification sent successfully');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending deliverable completion notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * NEW: Send notification for deliverable due date approaching
 * @param {Object} notificationData - Notification details
 */
export const sendDeliverableDueDateNotification = async (notificationData) => {
  try {
    const { teamId, deliverableName, teamName, dueDate, hoursRemaining } = notificationData;

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-file-upload-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          uploaderName: 'System',
          teamName: teamName,
          teamNumber: '',
          fileName: `Deliverable Due: ${deliverableName}`,
          timestamp: new Date()?.toISOString(),
          stage: 'Deliverable Due Date Alert',
          fileUrl: '',
          teamId: teamId,
          notificationType: 'deliverable_due_date',
          message: `Reminder: Deliverable "${deliverableName}" for team ${teamName} is due in ${hoursRemaining} hours (${new Date(dueDate)?.toLocaleDateString()})`
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('Deliverable due date notification failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('Deliverable due date notification sent successfully');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending deliverable due date notification:', error);
    return { success: false, error: error?.message };
  }
};

export default {
  getProjectStages: teamStageUploadsService?.getProjectStages,
  getTeamUploads: teamStageUploadsService?.getTeamUploads,
  uploadStageFile: teamStageUploadsService?.uploadStageFile,
  getFileUrl: teamStageUploadsService?.getFileUrl,
  deleteUpload: teamStageUploadsService?.deleteUpload,
  updateDescription: teamStageUploadsService?.updateDescription,
  uploadTeamStageFile: teamStageUploadsService?.uploadTeamStageFile,
  checkTeamDeadlines: checkTeamDeadlines,
  sendDeliverableCompletionNotification: sendDeliverableCompletionNotification,
  sendDeliverableDueDateNotification: sendDeliverableDueDateNotification
};